import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Images
import SNOW_FLAKE_IMG from "../../images/snowflake.png";
import WOC_POSTER_IMG from "../../images/events/winter-of-code/winter-of-code-7-2025.png";
import HACKTOBERFEST_POSTER_IMG from "../../images/events/Hacktoberfest/hacktoberfest-2024.png";
import FACEOFF_POSTER_IMG from "../../images/events/face-off/face-off-2024.jpg";

// Material-UI Components
import { Box, Grid, Typography } from "@mui/material";

// Material-UI Icons
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";

gsap.registerPlugin(ScrollTrigger);

const objectives = [
  {
    key: "Winter of Code",
    value: "Creating projects during the winter break",
  },
  {
    key: "Hacktoberfest",
    value:
      "Participate in the annual event to promote open-source contributions",
  },
  {
    key: "Face Off",
    value: "1V1 battle with competitive programming",
  },
];

const HorizontalScroll = () => {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;

    gsap.to(content, {
      x: () => -(content.scrollWidth - container.offsetWidth),
      ease: "none",
      scrollTrigger: {
        trigger: container,
        start: "top top",
        end: () => `+=${content.scrollWidth - container.offsetWidth}`,
        scrub: 1,
        pin: true,
        invalidateOnRefresh: true,
      },
    });

    return () => {
      ScrollTrigger.killAll();
    };
  }, []);

  useEffect(() => {
    // Function to handle mouse movement
    const handleMouseMove = (event) => {
      const elements = document.querySelectorAll(".moving-object");
      elements.forEach((element) => {
        const rect = element.getBoundingClientRect();
        const offsetX = (event.clientX - (rect.left + rect.width / 2)) / 50; // Minor horizontal movement
        const offsetY = (event.clientY - (rect.top + rect.height / 2)) / 50; // Minor vertical movement

        // Apply GSAP animation for smooth movement
        gsap.to(element, {
          x: offsetX,
          y: offsetY,
          duration: 0.3,
          ease: "power2.out",
        });
      });
    };

    // Attach the mousemove event listener
    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      // Cleanup the event listener on unmount
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        overflow: "hidden",
        position: "relative",
        width: "100vw",
        height: "100vh",
      }}
    >
      <div
        ref={contentRef}
        style={{
          display: "flex",
          whiteSpace: "nowrap",
        }}
      >
        {Array.from({ length: objectives.length }).map((_, index) => (
          <div
            key={index}
            style={{
              flex: "0 0 auto",
              width: "100vw",
              height: "100vh",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              // background: "white",
              color: "white",
              position: "relative",
            }}
          >
            <div
              className="moving-object"
              style={{
                position: "absolute",
                fontSize: "3rem",
                top: "6%",
                left: "6%",
              }}
            >
              <img
                src={SNOW_FLAKE_IMG}
                alt="SNOW_FLAKE_IMG"
                style={{ width: 100, transform: "rotate(45deg)" }}
              />
            </div>
            <div
              className="moving-object"
              style={{
                position: "absolute",
                fontSize: "3rem",
                top: "8%",
                left: "30%",
              }}
            >
              <img
                src={SNOW_FLAKE_IMG}
                alt="SNOW_FLAKE_IMG"
                style={{ width: 100, transform: "rotate(45deg)" }}
              />
            </div>
            <div
              className="moving-object"
              style={{
                position: "absolute",
                fontSize: "3rem",
                top: "80%",
                left: "80%",
              }}
            >
              <img
                src={SNOW_FLAKE_IMG}
                alt="SNOW_FLAKE_IMG"
                style={{ width: 100, transform: "rotate(40deg)" }}
              />
            </div>
            <div
              className="moving-object"
              style={{
                position: "absolute",
                fontSize: "3rem",
                top: "28%",
                left: "70%",
              }}
            >
              <img
                src={SNOW_FLAKE_IMG}
                alt="SNOW_FLAKE_IMG"
                style={{ width: 100, transform: "rotate(4deg)" }}
              />
            </div>
            <div
              className="moving-object"
              style={{
                position: "absolute",
                fontSize: "3rem",
                top: "80%",
                left: "20%",
              }}
            >
              <img
                src={SNOW_FLAKE_IMG}
                alt="SNOW_FLAKE_IMG"
                style={{ width: 100, transform: "rotate(45deg)" }}
              />
            </div>
            <Box
              sx={{
                maxWidth: "90%",
                zIndex: 1,
                fontSize: {
                  xs: "6vw",
                  sm: "6vw",
                  md: "6vw",
                  lg: "3.5rem",
                  xl: "3.5rem",
                },
                fontWeight: "bold",
                whiteSpace: "pre-wrap", // Allows multiline wrapping
                wordWrap: "break-word", // Ensures long words break if necessary
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Grid container paddingTop={6}>
                <Grid
                  xs={12}
                  sm={12}
                  md={12}
                  lg={8}
                  xl={8}
                  item
                  sx={{
                    display: "flex",
                    justifyContent: {
                      xs: "center",
                      sm: "center",
                      md: "center",
                      lg: "flex-start",
                      xl: "flex-start",
                    },
                    alignItems: {
                      xs: "center",
                      sm: "center",
                      md: "center",
                      lg: "flex-start",
                      xl: "flex-start",
                    },
                    flexDirection: "column",
                  }}
                >
                  <Box
                    onClick={() =>
                      navigate(
                        objectives[index].key === "Winter of Code"
                          ? "/events/winter-of-code"
                          : "/events"
                      )
                    }
                    sx={{
                      display: "flex",
                      justifyContent: {
                        xs: "flex-start",
                        sm: "flex-start",
                        md: "flex-start",
                        lg: "center",
                        xl: "center",
                      },
                      alignItems: "center",
                      cursor: "pointer",
                      color: "#012a4a",
                      background: "white",
                      width: "fit-content",
                      my: { xs: 5, sm: 5, md: 5, lg: 10, xl: 10 },
                      px: { xs: 3, sm: 3, md: 5, lg: 8, xl: 8 },
                      py: 2,
                      borderRadius: 5,
                      "&:hover": {
                        textDecoration: "underline",
                        "& .icon": {
                          display: "inline-block", // Show the icon on hover
                          transform: "rotate(360deg)", // Rotate the icon
                        },
                      },
                    }}
                  >
                    {objectives[index].key}
                    <ArrowOutwardRoundedIcon
                      className="icon"
                      sx={{
                        fontSize: {
                          xs: "6vw",
                          sm: "6vw",
                          md: "6vw",
                          lg: "3.5rem",
                          xl: "3.5rem",
                        },
                        ml: { xs: 1, sm: 1, md: 1, lg: 2, xl: 2 },
                        transition: "transform 0.5s ease-in-out", // Smooth rotation
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      m: 1,
                      display: "flex",
                      // justifyContent: "center",
                      justifyContent: {
                        xs: "center",
                        sm: "center",
                        md: "center",
                        lg: "flex-start",
                        xl: "flex-start",
                      },
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      fontWeight="bold"
                      sx={{
                        width: "80%",
                        textAlign: {
                          xs: "center",
                          sm: "center",
                          md: "center",
                          lg: "start",
                          xl: "start",
                        },
                        fontSize: {
                          xs: "4vw",
                          sm: "4vw",
                          md: "4vw",
                          lg: "3vw",
                          xl: "3vw",
                        },
                      }}
                    >
                      {objectives[index].value}
                    </Typography>
                  </Box>
                </Grid>
                <Grid
                  xs={12}
                  sm={12}
                  md={12}
                  lg={4}
                  xl={4}
                  item
                  paddingY={4}
                  sx={{
                    display: "flex",
                    justifyContent: {
                      xs: "center",
                      sm: "center",
                      md: "center",
                      lg: "flex-start",
                      xl: "flex-start",
                    },
                    alignItems: {
                      xs: "center",
                      sm: "center",
                      md: "center",
                      lg: "flex-start",
                      xl: "flex-start",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: {
                        xs: "center",
                        sm: "center",
                        md: "center",
                        lg: "flex-start",
                        xl: "flex-start",
                      },
                      alignItems: {
                        xs: "center",
                        sm: "center",
                        md: "center",
                        lg: "flex-start",
                        xl: "flex-start",
                      },
                      transition: "all 0.3s",
                      mx: 1,
                      "&:hover": {
                        transform: "rotate(2deg) scale(1.1)",
                        borderRadius: "6px",
                      },
                      img: {
                        maxWidth: {
                          xs: 300,
                          sm: 300,
                          md: 300,
                          lg: 400,
                          xl: 400,
                        },
                      },
                    }}
                  >
                    <img
                      className="moving-object"
                      src={
                        index === 0
                          ? WOC_POSTER_IMG
                          : index === 1
                          ? HACKTOBERFEST_POSTER_IMG
                          : FACEOFF_POSTER_IMG
                      }
                      alt="poster"
                      style={{
                        transform: `rotate(2deg)`,
                        borderRadius: "6px",
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HorizontalScroll;
